import React, { useState } from "react"
import ChartContainer from "components/Shared/ChartContainer"
import { useFetchData } from "components/Shared/Hooks/react-query/useFetchData"
import {
	CustomerProfilingChart,
	CustomerProfilingTable,
	getCustomerProfilingChartMetric,
	getCustomerProfilingTableMetric,
} from "../../service"
import {
	SegmentName,
	dataChartDefault,
	placeholderText,
} from "../../Shared/utils"
import { FiltersType } from "components/Shared/GlobalFilter/context/interfaces"
import Chart from "../../Chart"
import { MetricsBySegments } from "components/CustomerBehavior/CustomerProfiling/MetricsBySegments"
import AsyncMessageResponseHandler from "components/Shared/AsyncMessageResponseHandler"
import "./index.scss"
import Cards from "../../Cards"
import { Link } from "react-router-dom"
import * as paths from "constants/paths"
import { FormattedMessage } from "react-intl"
import { AGE } from "components/Shared/GlobalFilter/constants"
import { DateInterval } from "components/Shared/PhronesisCalendar/PhronesisCalendar.utils"
import ComingSoon from "../../ComingSoon"
import { CHART_COLORS, GENDER_COLORS } from "colors"

interface SegmentationSummaryProps {
	period: DateInterval
	filters: FiltersType
}

const SegmentationSummary: React.FC<SegmentationSummaryProps> = ({
	period,
	filters,
}) => {
	const [segment, setSegment] = useState<SegmentName>(AGE)
	const { data = [], isFetching } = useFetchData<Array<CustomerProfilingTable>>(
		"retrieveCustomerProfilingTable",
		getCustomerProfilingTableMetric,
		{ period, filters, segment }
	)
	const {
		data: dataChart = dataChartDefault,
		isFetching: isFetchingChartData,
		error: errorDataChart,
		isError: isErrorInDataChart,
		refetch: refetchDataChart,
	} = useFetchData<CustomerProfilingChart>(
		"retrieveCustomerProfilingChart",
		getCustomerProfilingChartMetric,
		{
			period,
			filters,
		},
		{
			enabled: true,
			retry: false,
		}
	)

	const { age, gender, psychographics } = dataChart

	const validateDataChartInfo = () => isFetchingChartData || age.length
	return (
		<AsyncMessageResponseHandler
			hasData={validateDataChartInfo()}
			error={errorDataChart}
			isError={isErrorInDataChart}
			isLoading={isFetchingChartData}
			retry={refetchDataChart}
			className="benchmark-error-container"
		>
			<div className="chart-container">
				<ChartContainer
					title={{
						id:
							"phronesis.customerbehavior.application.customerProfiling.habits.title",
					}}
				>
					<Cards />
				</ChartContainer>
			</div>

			<div className="chart-container">
				<ChartContainer
					title={{ id: placeholderText.psychographicsSegment }}
					body={{ id: placeholderText.psychographicsSegmentDescription }}
					isLoading={isFetchingChartData}
				>
					<Chart
						data={psychographics}
						colorPalette={CHART_COLORS}
						chartType="PieChart"
						type="psychographics"
					/>
					<div className="chart-link">
						<Link
							to={
								paths.ROOT_CONTEXT +
								paths.CUSTOMER_PROFILING_HABITS_SEGMENTATION
							}
						>
							<FormattedMessage id="phronesis.customerbehavior.application.customerProfiling.segmentationSummary.link.habits" />
						</Link>
					</div>
				</ChartContainer>
				<ChartContainer
					title={{ id: placeholderText.ageSegment }}
					body={{ id: placeholderText.ageSegmentDescription }}
					isLoading={isFetchingChartData}
				>
					<Chart
						data={age}
						colorPalette={CHART_COLORS}
						chartType="PieChart"
						type="age"
					/>
					<div className="chart-link">
						<Link
							to={
								paths.ROOT_CONTEXT +
								paths.CUSTOMER_PROFILING_DEMOGRAPHIC_SEGMENTATION
							}
						>
							<FormattedMessage id="phronesis.customerbehavior.application.customerProfiling.segmentationSummary.link.age" />
						</Link>
					</div>
				</ChartContainer>
				<ChartContainer
					title={{ id: placeholderText.genderSegment }}
					body={{ id: placeholderText.genderSegmentDescription }}
					isLoading={isFetchingChartData}
				>
					<Chart
						data={gender}
						colorPalette={GENDER_COLORS}
						chartType="PieChart"
						type="gender"
					/>
					<div className="chart-link">
						<Link
							to={
								paths.ROOT_CONTEXT +
								paths.CUSTOMER_PROFILING_DEMOGRAPHIC_SEGMENTATION
							}
						>
							<FormattedMessage id="phronesis.customerbehavior.application.customerProfiling.segmentationSummary.link.gender" />
						</Link>
					</div>
				</ChartContainer>

				<div className="main-chart-container">
					<ComingSoon isLoading={isFetchingChartData} />
				</div>
			</div>
			<div className="chart-container">
				<ChartContainer
					title={{
						id:
							"phronesis.customerbehavior.application.customerProfiling.metricsBySegments.title",
					}}
					body={{
						id:
							"phronesis.customerbehavior.application.customerProfiling.metricsBySegments.description",
					}}
					isLoading={isFetching}
				>
					<MetricsBySegments
						data={data}
						metricSegment={segment}
						setMetricSegment={(segment) => setSegment(segment)}
						segmentSelected={segment}
					/>
				</ChartContainer>
			</div>
		</AsyncMessageResponseHandler>
	)
}

export default SegmentationSummary
